import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../../services/api';
import { useAuth } from '../../hooks/AuthContext';
import { ErrorBox } from '../../components/Ui';
import { useTitle } from '../../hooks';
import { AuthFrame } from '../../design-system/components';
import { PasswordField } from '../../components/auth/PasswordField';
import { CountryField } from '../../components/auth/CountryField';

import { safeNext } from '../../shared/utils/auth';
export function Register() {
  useTitle('Create an account');
  const { user, signUp } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    country: '',
  });
  const [error, setError] = useState<ApiError>();
  const [busy, setBusy] = useState(false);
  const next = safeNext(params.get('next'));

  if (user) return <Navigate to={next} replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await signUp({
        username: form.username.trim(),
        password: form.password,
        country: form.country,
        ...(form.email.trim() ? { email: form.email.trim() } : {}),
      });
      navigate(next, { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, 'error', 'Something went wrong.'));
    } finally {
      setBusy(false);
    }
  };
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value });

  return (
    <AuthFrame>
      <div>
        <header className="auth-form-heading">
          <h1>Create your account</h1>
          <p>Start a private wiki. Share on your terms.</p>
        </header>
        <form className="auth-form" onSubmit={submit}>
          {error && <ErrorBox error={error} />}
          <div className="field">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              className="input"
              autoComplete="username"
              autoFocus
              required
              maxLength={39}
              pattern="[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,37}[a-zA-Z0-9])?"
              value={form.username}
              onChange={set('username')}
              aria-describedby="username-hint"
            />
            <span className="hint" id="username-hint">
              Letters, digits and hyphens. This appears in your wikis’ addresses.
            </span>
          </div>
          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              className="input"
              type="email"
              autoComplete="email"
              required
              maxLength={254}
              value={form.email}
              onChange={set('email')}
            />
          </div>
          <PasswordField
            value={form.password}
            creating
            onChange={(value) => setForm({ ...form, password: value })}
          />
          <CountryField value={form.country} onChange={(value) => setForm({ ...form, country: value })} />
          <button className="btn primary full-width" disabled={busy}>
            {busy ? 'Creating…' : 'Create account'}
          </button>
        </form>
        <p className="muted auth-footnote">
          Already have an account? <Link to={`/login?next=${encodeURIComponent(next)}`}>Sign in</Link>.
        </p>
      </div>
    </AuthFrame>
  );
}
